/**
 * printDocument — clean print path for the paginated editor.
 *
 * Replaces two broken approaches (see KNOWN_ISSUES BUG-001/BUG-002):
 *   - window.print() on the app page leaked nav/chat UI and clipped pages
 *   - html2canvas PDF export captured only the visible viewport
 *
 * Strategy: open a minimal standalone window containing ONLY the document
 * HTML plus print CSS (@page size/margins from the page setup), let the
 * browser's print engine paginate, and trigger the print dialog — where
 * "Save as PDF" produces a complete PDF.
 *
 * Details handled here:
 *   - Footnote numbers are decoration-rendered in the editor (never in
 *     getHTML()), so numbers are injected into the HTML before printing.
 *   - Header/footer zones render as position:fixed bars, which Chromium
 *     repeats on every printed page. {page} tokens are stripped — CSS page
 *     counters don't work outside @page margin boxes (unsupported in
 *     Chromium); use the browser print dialog's headers/footers for page
 *     numbers if needed.
 */

import type { ParsedHeaderFooterConfig } from "./ooxmlParser/types";
import type { FootnoteInfo } from "../extensions/FootnoteExtension";
import { PageSetup, pageDimensionsIn } from "./pageSetup";
import { sanitizeZoneHtml } from "./headerFooterZones";

export interface BuildPrintHtmlOptions {
  contentHtml: string;
  title: string;
  setup: PageSetup;
  headerFooter?: ParsedHeaderFooterConfig | null;
  footnotes?: FootnoteInfo[];
  /** Document watermark text (DRAFT / CONFIDENTIAL …). "" / null = none. */
  watermark?: string | null;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Strip {page}/{pages} tokens — not renderable in printed fixed bars —
 * and sanitize: this HTML reaches document.write, which EXECUTES embedded
 * scripts (unlike innerHTML), and the zone strings are user-controlled
 * persisted meta.
 */
function zoneHtml(html: string | undefined): string {
  return sanitizeZoneHtml((html || "").replace(/\{pages?\}/g, "").trim());
}

function renderFixedBar(
  section: { left?: string; center?: string; right?: string } | undefined,
  position: "top" | "bottom"
): string {
  const left = zoneHtml(section?.left);
  const center = zoneHtml(section?.center);
  const right = zoneHtml(section?.right);
  if (!left && !center && !right) return "";
  return (
    `<div class="print-bar print-bar-${position}">` +
    `<div class="print-bar-zone" style="text-align:left">${left}</div>` +
    `<div class="print-bar-zone" style="text-align:center">${center}</div>` +
    `<div class="print-bar-zone" style="text-align:right">${right}</div>` +
    `</div>`
  );
}

/**
 * Inject footnote numbers into the document HTML (they normally come from
 * editor decorations): refs get data-fn-num (rendered via CSS ::after) and
 * each note's first paragraph gets a leading number span.
 */
export function injectFootnoteNumbers(
  contentHtml: string,
  footnotes: FootnoteInfo[]
): string {
  if (!footnotes.length) return contentHtml;
  const byId = new Map(footnotes.map(f => [f.footnoteId, f.label]));
  const doc = new DOMParser().parseFromString(
    `<div id="__root">${contentHtml}</div>`,
    "text/html"
  );
  const root = doc.getElementById("__root")!;

  root.querySelectorAll("sup[data-footnote-id]").forEach(sup => {
    const label = byId.get(sup.getAttribute("data-footnote-id") || "");
    if (label) sup.setAttribute("data-fn-num", label);
  });

  root.querySelectorAll("[data-footnote-item]").forEach(item => {
    const label = byId.get(item.getAttribute("data-footnote-id") || "");
    if (!label) return;
    const span = doc.createElement("span");
    span.className = "fn-num";
    span.textContent = `${label}. `;
    const firstParagraph = item.querySelector("p");
    if (firstParagraph) {
      firstParagraph.insertBefore(span, firstParagraph.firstChild);
    } else {
      item.insertBefore(span, item.firstChild);
    }
  });

  return root.innerHTML;
}

export function buildPrintHtml(options: BuildPrintHtmlOptions): string {
  const { setup, title, headerFooter } = options;
  const { widthIn, heightIn } = pageDimensionsIn(setup);
  const m = setup.margins;

  const content = injectFootnoteNumbers(
    options.contentHtml,
    options.footnotes || []
  );

  const headerBar = renderFixedBar(headerFooter?.defaultHeader, "top");
  const footerBar = renderFixedBar(headerFooter?.defaultFooter, "bottom");

  // Watermark: a fixed, tiled overlay repeats on every printed page (same
  // mechanism as the header/footer bars). Sits behind the text via z-index.
  const wmText = (options.watermark || "").trim();
  const watermarkDiv = wmText
    ? (() => {
        const esc = wmText
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        const svg =
          `<svg xmlns='http://www.w3.org/2000/svg' width='480' height='320'>` +
          `<text x='240' y='170' fill='rgba(120,120,120,0.18)' ` +
          `font-family='Arial, sans-serif' font-size='46' font-weight='700' ` +
          `text-anchor='middle' transform='rotate(-32 240 160)'>${esc}</text></svg>`;
        const url = `data:image/svg+xml,${encodeURIComponent(svg)}`;
        return `<div class="print-watermark" style="background-image:url(&quot;${url}&quot;)"></div>`;
      })()
    : "";

  // Reserve room for the repeated bars inside the content area
  const topPad = headerBar ? "0.35in" : "0";
  const bottomPad = footerBar ? "0.35in" : "0";

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  @page {
    size: ${widthIn}in ${heightIn}in;
    margin: ${m.top}in ${m.right}in ${m.bottom}in ${m.left}in;
  }
  html, body {
    margin: 0;
    padding: 0;
  }
  body {
    font-family: "Times New Roman", Times, serif;
    font-size: 12pt;
    line-height: 1.15;
    color: #000;
    padding-top: ${topPad};
    padding-bottom: ${bottomPad};
  }
  p { margin: 0; }
  h1, h2, h3, h4, h5, h6 { margin: 0.4em 0 0.2em; page-break-after: avoid; }
  table { border-collapse: collapse; width: 100%; page-break-inside: avoid; }
  td, th { border: 0.5pt solid #444; padding: 3pt 5pt; vertical-align: top; }
  img { max-width: 100%; height: auto; }
  blockquote { margin: 0.4em 0 0.4em 0.5in; }
  pre { font-family: "Courier New", monospace; font-size: 10pt; white-space: pre-wrap; }
  a { color: #1155cc; }
  ul, ol { margin: 0.2em 0; }
  hr { border: none; border-top: 0.75pt solid #000; }
  hr.tiptap-page-break, [data-type="page-break"] { border: none; page-break-after: always; }

  /* Comments/track changes: neutral in print */
  .tiptap-comment, [data-comment-id] { background: none !important; }

  /* Footnotes */
  sup.footnote-ref { font-size: 0.75em; line-height: 1; }
  sup.footnote-ref::after { content: attr(data-fn-num); }
  section[data-footnotes-section] {
    margin-top: 18pt;
    font-size: 10pt;
    page-break-inside: avoid;
  }
  section[data-footnotes-section]::before {
    content: "";
    display: block;
    width: 1.5in;
    border-top: 0.75pt solid #444;
    margin-bottom: 6pt;
  }
  [data-footnote-item] { margin-bottom: 3pt; }
  [data-footnote-item] .fn-num { font-size: 9pt; }

  /* Repeated header/footer bars (fixed elements repeat per printed page) */
  .print-bar {
    position: fixed;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    gap: 12pt;
    font-size: 10pt;
    line-height: 1.2;
  }
  .print-bar-top { top: 0; }
  .print-bar-bottom { bottom: 0; }
  .print-bar-zone { flex: 1; }
  .print-bar img { max-height: 0.4in; vertical-align: middle; }

  /* Watermark: fixed + tiled → repeats on every printed page, behind text. */
  .print-watermark {
    position: fixed;
    inset: 0;
    z-index: -1;
    background-repeat: repeat;
    background-position: center top;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
</style>
</head>
<body>
${watermarkDiv}
${headerBar}
${footerBar}
<div class="print-content">
${content}
</div>
</body>
</html>`;
}

/**
 * Open the print window and (optionally) trigger the print dialog once
 * fonts/images have loaded. Returns false when the popup was blocked.
 */
export function openPrintWindow(
  html: string,
  options: { autoPrint?: boolean } = {}
): boolean {
  const win = window.open("", "_blank", "width=920,height=1100");
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  if (options.autoPrint !== false) {
    const triggerPrint = () => {
      try {
        win.focus();
        win.print();
      } catch {
        // Window may have been closed before load finished
      }
    };
    // Give images/fonts a beat to load before opening the dialog
    if (win.document.readyState === "complete") {
      setTimeout(triggerPrint, 250);
    } else {
      win.addEventListener("load", () => setTimeout(triggerPrint, 250));
    }
  }
  return true;
}
